import deepEqual from "fast-deep-equal";
import { atom, useAtom } from "jotai";
import { atomFamily, atomWithStorage } from "jotai/utils";
import React from "react";

import {
  type AccessionListFilter,
  filters as accessionListFiltersSchema,
} from "@blis/shared/schemas/accession";

import { deepMerge } from "@/lib/deep-merge";
import type {
  TAccessionStatus,
  TPriority,
  TTestType,
  TView,
} from "@/lib/types";

const defaultFilters: AccessionListFilter = {
  myAccessions: false,
  status: [] as TAccessionStatus[],
  holdStatus: [] as ("active" | "resolved" | "canceled")[],
  client: [] as number[],
  sharedClient: [] as number[],
  billToOrg: [] as number[],
  splitBillingOrgs: [] as number[],
  priority: [] as TPriority[],
  test: [] as number[],
  panel: [] as number[],
  testType: [] as TTestType[],
  accessionIds: [] as number[],
  labs: [] as number[],
  hasSendOut: false,
};

const defaultView: TView = {
  id: 0,
  slug: "default",
  display: "TABLE" as const,
  filters: defaultFilters,
  name: "Default",
  createdById: -1,
  createdAt: new Date(),
  updatedAt: new Date(),
  displayOrder: null,
  limit: 500,
};

/** Ensures every filter field exists; avoids deepMerge corrupt output when storage is not an object. */
function mergeStoredFilters(stored: unknown): AccessionListFilter {
  const parsed = accessionListFiltersSchema.safeParse(stored);
  if (!parsed.success) {
    return defaultFilters;
  }
  return parsed.data;
}

type ViewContextValue = {
  view: TView;
  filters: AccessionListFilter;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  isEditingLoading: boolean;
  setIsEditingLoading: (isEditingLoading: boolean) => void;
  setFilters: React.Dispatch<React.SetStateAction<AccessionListFilter>>;
  resetFilters: () => void;
  additionalFilters: AccessionListFilter;
  setAdditionalFilters: React.Dispatch<
    React.SetStateAction<AccessionListFilter>
  >;
  dirtyFilters: boolean;
  settings: ViewSettings;
  setSettings: React.Dispatch<React.SetStateAction<Partial<ViewSettings>>>;
  dirtySettings: boolean;
  resetSettings: () => void;
};

const ViewContext = React.createContext<ViewContextValue | null>(null);

export function ViewProvider({
  children,
  view = defaultView,
}: {
  children: React.ReactNode;
  view?: TView;
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [isEditingLoading, setIsEditingLoading] = React.useState(false);
  const { settings, setSettings, resetSettings } = useViewSettings(view);

  const {
    filters,
    setFilters,
    resetFilters,
    additionalFilters,
    setAdditionalFilters,
  } = useViewFilters(view, isEditing);

  const dirtyFilters = React.useMemo(() => {
    return !deepEqual(filters, view.filters);
  }, [filters, view.filters]);

  const dirtySettings =
    view.display !== settings.display || view.limit !== settings.limit;

  return (
    <ViewContext.Provider
      value={{
        view,
        isEditing,
        setIsEditing,
        isEditingLoading,
        setIsEditingLoading,
        filters,
        setFilters,
        dirtyFilters,
        resetFilters,
        additionalFilters,
        setAdditionalFilters,
        settings,
        setSettings,
        dirtySettings,
        resetSettings,
      }}
    >
      {children}
    </ViewContext.Provider>
  );
}

export function useView() {
  const context = React.useContext(ViewContext);
  if (!context) {
    throw new Error("useView must be used within a ViewProvider");
  }
  return context;
}

function useViewFilters(view: TView, isEditing: boolean) {
  // there are 3 states of filters:
  // 1. view.filters: the filters that are saved in the database
  // 2. editingFilters: the filters that are being edited
  // 3. additionalFilters: the filters that are added in the UI, they may be saved or not
  const [editingFilters, setEditingFilters] = React.useState(defaultFilters);
  const [additionalFilters, setAdditionalFilters] = useAtom(
    additionalFilterAtomFamily(view.id),
  );

  const normalizedAdditionalFilters = React.useMemo(
    () => mergeStoredFilters(additionalFilters),
    [additionalFilters],
  );

  const savedFilters = React.useMemo(
    () => mergeStoredFilters(view.filters),
    [view.filters],
  );

  // when editing is true, editingFilters is used to store the edited filters
  React.useEffect(() => {
    if (isEditing) {
      setEditingFilters(savedFilters);
    } else {
      setEditingFilters(defaultFilters);
    }
  }, [isEditing, savedFilters]);

  const toMerge = React.useMemo(() => {
    const merged = { ...normalizedAdditionalFilters };
    if (savedFilters.hasSendOut) {
      merged.hasSendOut = true;
    }
    if (savedFilters.myAccessions) {
      merged.myAccessions = true;
    }
    return merged;
  }, [
    savedFilters.hasSendOut,
    savedFilters.myAccessions,
    normalizedAdditionalFilters,
  ]);

  // when editing is false, savedFilters and additionalFilters are merged to get the active filters
  const filters = isEditing ? editingFilters : deepMerge(savedFilters, toMerge);

  const setFilters = React.useMemo(() => {
    const rawSet = isEditing ? setEditingFilters : setAdditionalFilters;
    return (updater: React.SetStateAction<AccessionListFilter>) => {
      rawSet((prev) => {
        const base = mergeStoredFilters(prev);
        return typeof updater === "function"
          ? updater(base)
          : mergeStoredFilters(updater);
      });
    };
  }, [isEditing, setAdditionalFilters]);

  const resetFilters = React.useCallback(() => {
    setEditingFilters(mergeStoredFilters(view.filters));
    setAdditionalFilters(defaultFilters);
  }, [view.filters, setAdditionalFilters]);

  return {
    filters,
    editingFilters,
    setFilters,
    resetFilters,
    additionalFilters: normalizedAdditionalFilters,
    setAdditionalFilters,
  };
}

function useViewSettings(view: TView) {
  const [settings, setSettings] = useAtom(displayAtomFamily(view.id));

  const resetSettings = React.useCallback(() => {
    setSettings({
      display: view.display,
      dateRelative: "actual",
      limit: view.limit,
    });
  }, [view.display, view.limit, setSettings]);

  return {
    settings: deepMerge(
      { display: view.display, dateRelative: "actual", limit: view.limit },
      settings,
    ) as ViewSettings,
    setSettings,
    resetSettings,
  };
}

const additionalFilterAtomFamily = atomFamily((viewId: number) =>
  atomWithStorage(
    `blis.accession.list.filters.${viewId}`,
    defaultFilters,
    undefined,
    { getOnInit: true },
  ),
);

type ViewSettings = {
  display: TView["display"];
  dateRelative: "actual" | "relative";
  limit: number;
};

const displayAtomFamily = atomFamily((_viewId: number) =>
  atom<Partial<ViewSettings>>({}),
);

// this was used previously to persist the filters and display settings

// const accessionFiltersFamily = atomFamily(
//   (view: { id: number; isEditing?: boolean }) =>
//     atomWithStorage(
//       `blis.accession.list.filters.${view.id}`,
//       defaultSettings,
//       undefined,
//       { getOnInit: true },
//     ),
// );

// export function deletePersistedFilters(view: TView) {
//   localStorage.removeItem(`blis.accession.list.filters.${view.id}`);
// }

// const accessionDisplayFamily = atomFamily((view: TView) =>
//   atomWithStorage(
//     `blis.accession.list.display.${view.id}`,
//     { take: 50, display: view.display, dateRelative: "actual" },
//     undefined,
//     { getOnInit: true },
//   ),
// );
